import { useRef, useState } from 'react';
import { useBillingClient } from 'client/client-hook';
import useAuth from 'hooks/useAuth';
import { analytics } from 'lib/analytics';
import { subscriptionValue } from 'lib/analytics/subscription-value';
import type { LibSubscription } from 'lib/billing/pbAdapter';
import { useBillingStore } from 'sections/account-settings/subscriptions/checkout-v2/store';
import { CANCEL_ERROR_FALLBACK, partialCancelMessage } from './content';
import { useLeaveDialog } from './leaveDialogStore';

// grpc-web puts the readable text in `details`, so that wins.
const errorDetail = (cause: unknown) => (cause as any)?.details || (cause as any)?.message || CANCEL_ERROR_FALLBACK;

type CancelSubscriptions = {
  /** Still to cancel: a retry after a partial failure sends only these. */
  pending: LibSubscription[];
  busy: boolean;
  error: string;
  /** Resolves with everything cancelled this visit once none is left, or null while some remain. */
  cancelAll: () => Promise<LibSubscription[] | null>;
};

/** Cancels each subscription whole at period end, one by one, so a failure is pinned to its subscription. */
export function useCancelSubscriptions(targets: LibSubscription[]): CancelSubscriptions {
  const { user } = useAuth() as any;
  const userId: string = user?.tcId || '';
  const getBillingClient = useBillingClient();
  const fetchBilling = useBillingStore((state) => state.fetch);
  const markEnding = useLeaveDialog((state) => state.markEnding);
  // Billing reports the end date only after Stripe's webhook, so a retry must not send these again.
  const [cancelled, setCancelled] = useState<LibSubscription[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);

  const pending = targets.filter((target) => !cancelled.some((done) => done.id === target.id));

  const cancelAll = async () => {
    if (busyRef.current || !userId || !pending.length) return null;
    busyRef.current = true;
    setBusy(true);
    setError('');
    const done: LibSubscription[] = [];
    let lastCause: unknown;
    try {
      const client = await getBillingClient();
      for (const subscription of pending) {
        try {
          await client.cancelSubscription({ userId, subscriptionId: subscription.id });
          done.push(subscription);
          analytics.subscriptionCanceled({ ...subscriptionValue(subscription), is_partial: false });
        } catch (cause) {
          lastCause = cause;
        }
      }
    } catch (cause) {
      lastCause = cause;
    }

    markEnding(done.map((subscription) => subscription.id));
    void fetchBilling(userId, { force: true });
    const all = [...cancelled, ...done];
    setCancelled(all);
    busyRef.current = false;
    setBusy(false);
    if (done.length === pending.length) return all;
    const failed = pending.length - done.length;
    setError(done.length ? partialCancelMessage(failed, pending.length, errorDetail(lastCause)) : errorDetail(lastCause));
    return null;
  };

  return { pending, busy, error, cancelAll };
}
